import type { Tutorial } from '../types'

const IMG = '/images/tutorials/mobile/wellness-goals'

export const wellnessGoalsTutorial: Tutorial = {
  slug: 'wellness-goals',
  title: 'Wellness Goals',
  subtitle: 'Turn insight into a daily habit you actually track',
  date: '2026-07-09',
  category: 'Core Features',
  platform: 'mobile',
  tags: ['Wellness Goals', 'check-in', 'streak', 'accountability', 'habits'],
  description:
    'Create goals, check in with three taps, and watch a streak build — the daily behavior loop that turns Coach insight into real progress.',
  highlights: [
    'Create from scratch or straight from a Coach suggested-goal card.',
    'Quick check-in is deliberately not a chat — Yes / Partly / No plus an optional note.',
    'Streak grid mirrors the web dashboard so progress is visible at a glance.',
  ],
  coverImage: `${IMG}/01_goals_list.png`,
  sections: [
    {
      heading: 'Creating a goal',
      paragraphs: [
        'Tap **+** from the goals list and a short form opens — a title, an optional description of what success looks like, and an optional target date. No required fields beyond the title.',
        'Goals can also be created straight from a Wellness Coach conversation — the Coach pre-fills the title and rationale from the genetic insight that prompted it.',
      ],
      image: {
        src: `${IMG}/00_new_goal_form.png`,
        alt: 'New wellness goal creation form',
        caption: 'New goal form',
      },
    },
    {
      heading: 'The goals list',
      paragraphs: [
        'Once a goal exists, it shows up as a card with its category, current status, and how long it\'s been since the last check-in.',
        'The green checkmark icon on each card is a one-tap shortcut into that goal\'s check-in — no need to open the goal first.',
      ],
      image: {
        src: `${IMG}/01_goals_list.png`,
        alt: 'Wellness Goals list with multiple active goals',
        caption: 'Wellness Goals list',
      },
    },
    {
      heading: 'The goal detail: streak at a glance',
      paragraphs: [
        'Tapping into a goal shows its status, a big **Check in** button, and a check-in streak grid going back several weeks — the same "did I show up" pattern used by habit-tracking apps, mirrored from the web dashboard.',
      ],
      image: {
        src: `${IMG}/02_goal_detail_streak.png`,
        alt: 'Goal detail page with check-in streak grid',
        caption: 'Goal detail with check-in streak',
      },
    },
    {
      heading: 'Checking in: fast, not conversational',
      paragraphs: [
        'This is deliberately **not** a chat. Tapping **Check in** opens a short sheet: did you follow through today (Yes / Partly / No), plus an optional note. Three taps and done.',
        'This calls `POST /goal-checkin/quick` directly — no LLM in the loop, so it\'s fast and works the same from the app, a reminder push, or a magic-link email.',
      ],
      image: {
        src: `${IMG}/03_checkin_sheet.png`,
        alt: 'Quick check-in sheet with Yes, Partly, and No options',
        caption: 'Quick check-in sheet',
      },
    },
    {
      heading: 'After submitting',
      paragraphs: [
        'The check-in posts immediately, the streak grid updates, and a same-day debounce prevents accidental double-logging if you tap Check in twice in a row.',
      ],
      image: {
        src: `${IMG}/05_checkin_submitted.png`,
        alt: 'Check-in submitted with updated streak grid',
        caption: 'Check-in submitted, streak updated',
      },
    },
  ],
  whyMatters:
    'The Coach is where insight happens; Goals is where behavior happens. By keeping the check-in to three taps and pulling the streak visualization straight into the goal, Livewello asks for almost nothing each day while still building a real history — the same history the Coach later reads back to ground its advice, and that accountability partners see when they check in on someone they\'re supporting.',
  nextSlug: 'health-records',
  relatedSlugs: ['wellness-coach', 'accountability-partners'],
  meta: {
    description:
      'See how Wellness Goals works: create a goal, quick check-in in three taps, and a streak that builds real accountability history.',
    ogImage: `${IMG}/01_goals_list.png`,
  },
}
